/**
 * Version information and release notes (#3314, #3315).
 *
 * Shapes mirror services/staff-api/src/routes/version.ts and
 * lib/release-notes.ts.
 */

import { api } from '@/lib/api';

export type ReleaseNoteCategory = 'new' | 'improved' | 'fixed';

export interface ReleaseNoteText {
  readonly title: string;
  readonly body?: string;
}

export interface ReleaseNoteEntry {
  readonly id: string;
  readonly addedIn: string;
  readonly date: string;
  readonly category: ReleaseNoteCategory;
  readonly actionRequired: boolean;
  readonly pr?: number;
  readonly fi: ReleaseNoteText;
  readonly en: ReleaseNoteText;
}

export interface PublicVersion {
  readonly version: string;
  readonly sha: string | null;
  readonly installedAt: string;
}

export interface VersionInfo extends PublicVersion {
  readonly builtAt: string | null;
  readonly environment: 'production' | 'test';
  /** Production's version; only a test environment reports it. */
  readonly production: PublicVersion | null;
}

export type VersionChangesStatus = 'ok' | 'unknown_from' | 'unknown_to' | 'unavailable';

export interface VersionChanges {
  readonly from: string | null;
  readonly to: string | null;
  readonly status: VersionChangesStatus;
  readonly entries: readonly ReleaseNoteEntry[];
}

export function fetchVersionInfo(): Promise<VersionInfo> {
  return api.get<VersionInfo>('/version');
}

/**
 * Release notes between two versions. Without `from` the server compares with
 * production (test environment); without `to` it uses its own build.
 */
export function fetchVersionChanges(params: { from?: string; to?: string } = {}): Promise<VersionChanges> {
  const query = new URLSearchParams();
  if (params.from) query.set('from', params.from);
  if (params.to) query.set('to', params.to);
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return api.get<VersionChanges>(`/version/changes${suffix}`);
}
